enum AICommands {
  activate = '+ai',
  deactivate = '-ai',
  empty = '',
}

enum CartItemAdditionTypes {
  human = 'human',
  AI = 'ai',
}

enum EventTypes {
  start = 'start',
  audio = 'audio',
  text = 'text',
  info = 'info',
  endSession = 'end_session',
  order = 'order',
  orderStatus = 'order_status',
  loyalty = 'loyalty',
  loyaltyStatus = 'loyalty_status',
  hypothesis = 'hypothesis',
  startSession = 'start_session',
  carExit = 'car_exit',
  carEnter = 'car_enter',
  check = 'check',
  coupon = 'coupon',
  TTSRequest = 'tts_request',
  TTSOff = 'tts_off',
  TTSOn = 'tts_on',
  error = 'error',
  staffIntervention = 'staff_intervention',
  agentFirstInterception = 'agent_first_interception',
  agentInterception = 'agent_interception',
  connect = 'connect',
  disconnect = 'disconnect',
  aiEscalation = 'ai_escalation',
  hitlSessionStart = 'hitl_session_start',
  hitlSessionEnd = 'hitl_session_end',
  aiStatus = 'ai_status',
  // HITL 3.0: consolidated per-turn AI session state. Carries the conversation
  // turn count, customer sentiment and reused-answer info surfaced in the Pulse
  // card. Consumed in the ai slice (mirrors ai_status / ai_escalation).
  aiSessionState = 'ai_session_state',
  agentActivity = 'agent_activity',
  agentFirstActivity = 'agent_first_activity',
  // HITL 3.0: agent performance metric (discriminated by data.metric). First
  // metric is agent_response_time — emitted when the agent's per-turn response
  // time exceeds the configured limit during a takeover.
  agentMetric = 'agent_metric',
  hitlEventConnection = 'hitl_event_connection',
  cancelOrder = 'cancel_order',
  languageSwitch = 'language_switch',
  asrIncorrect = 'asr_incorrect',
  transcriptHighlight = 'transcript_highlight',
  // secondary_asr: AI-side promoted (higher-confidence) ASR commit. Carries the
  // committed text in data.payload (no correlation id); matched to the CX
  // transcript by text and flagged with a promoted icon when the secondary-ASR
  // flags (AI-side + HITL-side, see config.flags) are both on.
  asrPromotedText = 'asr_promoted_text',
  // HITL-18: the human agent's live working cart, streamed to the AI as
  // read-only input during takeover (co-pilot). Routed to AI consumers only,
  // never to POS — it is NOT a submit (see `order`).
  agentCart = 'agent_cart',
  // AI -> HITL advisory suggestion (the "advise" side of the co-pilot). One
  // event family discriminated by data.suggestion_type; consumed by the
  // aiSuggestions slice and rendered as a one-tap suggestion.
  aiSuggestion = 'ai_suggestion',
  // HITL -> AI response to an ai_suggestion: the human agent explicitly
  // accepted or dismissed the suggestion. Emitted from useAISuggestionActions
  // (hitl_3_0 only), correlated back to the suggestion via suggestion_id /
  // request_id.
  agentSuggestionResponse = 'agent_suggestion_response',
  // HITL-1492 Pillar 3: an agent-filed issue report. This websocket path is a
  // best-effort real-time MIRROR — the REST POST to ${EVENT_API}/v1/event is the
  // source of truth, because the socket is closed at the moment a classification is
  // submitted at the task-router boundary. Mirrored at revision 0 only; consumers
  // dedupe on (issue_id, revision), NOT on envelope id (the REST and websocket
  // copies carry different envelope ids and timestamps by construction).
  issueReport = 'issue_report',
}

enum AgentTypes {
  HITL = 'HITL',
  RULAI = 'RULAI',
  prestoAI = 'PRESTOAI',
  llm = 'LLM',
  orderBoard = 'ORDERBOARD',
  restaurantStaff = 'RESTAURANTSTAFF',
  taskManager = 'TASK_MANAGER',
  dialogManager = 'DIALOGMANAGER',
  menuModule = 'MENU_MODULE',
}

enum StatusResponseTypes {
  success = 'Success',
  error = 'Error',
}

enum CheckResponseTypes {
  ok = 'ok',
  error = 'error',
  unknown = 'unknown',
}

enum CouponResponseTypes {
  ok = 'ok',
  completed = 'COMPLETED',
  unknown = 'UNKNOWN',
  error = 'error',
}

enum EventSources {
  prestoVoice = 'PRESTOVOICE',
}

const TTS_PRIORITY_BY_INTENT: Record<string, number> = {
  '#greet': 500,
  '#acknowledge': 700,
  '#ask-repeat': 500,
  '#ask-moment': 700,
  '#request-staff': 100,
  '#continue': 500,
  '#confirm': 500,
  '#readback': 200,
  '#window': 200,
};

const TTS_PRIORITY_DEFAULT = 500;

export {
  AICommands,
  AgentTypes,
  CartItemAdditionTypes,
  CheckResponseTypes,
  CouponResponseTypes,
  EventSources,
  EventTypes,
  StatusResponseTypes,
  TTS_PRIORITY_BY_INTENT,
  TTS_PRIORITY_DEFAULT,
};
